/**
 * Pure helpers for the Bill flat contracts page (tracker item 34). Amounts
 * come from the server as decimal strings; the only client arithmetic is the
 * total of the selected rows, done in integer cents.
 */

import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { centsToDecimal, toCents } from './financeChargeUi';
import { invoiceDate, invoiceMoney } from './invoiceUi';

export const FLAT_CONTRACT_BILLING_PATH = '/invoices/flat-contracts';

/** A preview row's selection key: one schedule period. */
export function rowKey(row: Pick<types.FlatContractPreviewRow, 'scheduleId' | 'periodStartDate'>): string {
  return `${row.scheduleId}|${row.periodStartDate}`;
}

/** The bill item a selection key stands for. */
export function keyToItem(key: string): types.FlatContractBillItem {
  const [scheduleId, periodStartDate] = key.split('|');
  return { scheduleId, periodStartDate };
}

/** Keys of the rows that can be billed. */
export function billableKeys(rows: types.FlatContractPreviewRow[]): string[] {
  return rows.filter((row) => row.billable).map(rowKey);
}

/**
 * Selected by default: the current periods that are due. Missed earlier
 * periods are listed but must be ticked on purpose (no back-billing flood).
 */
export function defaultSelection(rows: types.FlatContractPreviewRow[]): string[] {
  return rows.filter((row) => row.billable && row.isCurrent).map(rowKey);
}

/** Select-all checkbox: every billable row, or nothing when all are selected. */
export function toggleAllRows(rows: types.FlatContractPreviewRow[], selected: string[]): string[] {
  const keys = billableKeys(rows);
  return keys.length > 0 && keys.every((key) => selected.includes(key)) ? [] : keys;
}

/** Total (amount + tax) of the selected billable rows. */
export function selectedTotal(rows: types.FlatContractPreviewRow[], selected: string[]): string {
  const cents = rows
    .filter((row) => row.billable && selected.includes(rowKey(row)))
    .reduce((sum, row) => sum + toCents(row.total), 0);
  return centsToDecimal(cents);
}

export function billConfirmMessage(count: number, total: string, missed: number): string {
  const invoices = `${count} flat contract ${count === 1 ? 'invoice' : 'invoices'}`;
  const late = missed > 0 ? ` ${missed} of them ${missed === 1 ? 'is' : 'are'} for a missed earlier period.` : '';
  return `Create and post ${invoices} totaling ${invoiceMoney(total)}, dated today?${late} Each period can only be billed once.`;
}

/** "10/01/2026 – 10/31/2026" */
export function periodLabel(row: Pick<types.FlatContractPreviewRow, 'periodStartDate' | 'periodEndDate'>): string {
  return `${invoiceDate(row.periodStartDate)} – ${invoiceDate(row.periodEndDate)}`;
}

const STATUS_LABELS: Record<string, string> = {
  DUE: 'Due',
  ALREADY_BILLED: 'Already billed',
  OVERLAPS_BILLED_PERIOD: 'Overlaps a billed period',
  NOT_A_DUE_PERIOD: 'Not a due period',
  SCHEDULE_NOT_ACTIVE: 'Schedule not active',
  NOT_FLAT_CONTRACT: 'Not a flat contract',
  ERROR: 'Error',
};

/** Label for a row status or a skip reason. */
export function flatContractStatusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return STATUS_LABELS[status] ?? status;
}

export const MODE_LABELS: Record<string, string> = { MANUAL: 'Manual', AUTOMATIC: 'Automatic' };

/** What the tenant's mode means for this page. */
export function modeDescription(mode: string): string {
  return mode === 'AUTOMATIC'
    ? 'Automatic billing is on: the server bills each contract\'s current period when its recurring generator runs. Use this page to catch up missed periods or bill a period before the next run.'
    : 'Manual billing: nothing is billed until you bill it here. An administrator can turn on automatic billing under Settings > Company.';
}

/**
 * A user-facing message for a preview / bill failure. Server messages are
 * already readable; known codes say what to do next.
 */
export function flatContractError(err: unknown, fallback: string): string {
  switch (getErrorCode(err)) {
    case 'SCHEDULE_NOT_FOUND':
      return 'One of the selected schedules no longer exists. Nothing was billed. Refresh the preview and try again.';
    case 'FLAT_CONTRACT_ACCOUNT_MISSING':
      return 'The 4000 Service Revenue account is missing from the chart of accounts. Nothing was billed. Ask an administrator to add it.';
    default:
      return getErrorMessage(err, fallback);
  }
}
